import { useEffect, useRef, useState } from "react";
import { HumanSupportBanner } from "@/components/recruiter/HumanSupport";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Award, Mic, Send, Square, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, PageHeader, Spinner } from "@/components/Common";
import { Rich } from "@/components/Rich";
import { JobPicker } from "@/components/JobPicker";
import { api, errMsg, humanize, resolveUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

const msgText = (m) => (typeof m === "string" ? m : m.text || m.content || "");
const isUser = (m) => m.role === "user" || m.from === "user";

const speak = async (text) => {
  try {
    const { data } = await api.post("/ai/speak", { text });
    const url = resolveUrl(data.url);
    const blob = (await api.get(url, { baseURL: "", responseType: "blob" })).data;
    new Audio(URL.createObjectURL(blob)).play();
  } catch (e) { toast.error(errMsg(e)); }
};

const useRecorder = (onText) => {
  const [rec, setRec] = useState(null);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const fd = new FormData();
        fd.append("audio", new Blob(chunks, { type: "audio/webm" }), "voice.webm");
        try { const { data } = await api.post("/ai/transcribe", fd); onText(data.text || data.transcript || ""); } catch (e) { toast.error(errMsg(e)); }
      };
      mr.start();
      setRec(mr);
    } catch { toast.error("Microphone not available"); }
  };
  const stop = () => { rec?.stop(); setRec(null); };
  return { recording: !!rec, start, stop };
};

export default function Chat() {
  const { modeId } = useParams();
  const chat = useLocation().state?.chat;
  const [jobId, setJobId] = useState("");
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [score, setScore] = useState(null);
  const endRef = useRef(null);
  const { recording, start: startRec, stop: stopRec } = useRecorder((t) => setText((x) => (x ? `${x} ${t}` : t)));
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/ai/chat/${modeId}/start`, { job_id: jobId || null }); setSid(data.session_id); setMsgs(data.messages || []); setScore(null); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || busy) return;
    const t = text.trim();
    setText("");
    setMsgs((m) => [...m, { role: "user", text: t }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/ai/chat/session/${sid}/message`, { text: t });
      if (Array.isArray(data.messages)) setMsgs(data.messages);
      else { const r = data.reply || data.message || data; setMsgs((m) => [...m, typeof r === "string" ? { role: "assistant", text: r } : { role: "assistant", ...r }]); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doScore = async () => {
    setBusy(true);
    try { setScore((await api.post(`/ai/chat/session/${sid}/score`)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const title = chat?.title || humanize(modeId);
  return (
    <div>
      <Link to="/ai-studio" className="mb-4 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-300" data-testid="back-to-studio"><ArrowLeft className="h-4 w-4" />AI Studio</Link>
      <PageHeader title={title} subtitle={chat?.subtitle}>
        {sid && (chat?.scorable ?? true) && <Button variant="outline" onClick={doScore} disabled={busy || msgs.length < 2} data-testid="score-session-button"><Award className="h-4 w-4 text-[#D4AF37]" />Score session</Button>}
        {sid && <Button variant="ghost" onClick={() => { setSid(null); setMsgs([]); setScore(null); }} data-testid="new-session-button">New session</Button>}
      </PageHeader>
      <HumanSupportBanner where="companion" />
      {!sid ? (
        <Card className="max-w-xl">
          <p className="mb-4 text-sm text-zinc-400">Optionally anchor this conversation to a role in your tracker.</p>
          <JobPicker value={jobId} onChange={setJobId} optional />
          <Button onClick={start} disabled={busy} className="mt-5 w-full" data-testid="start-chat-button">{busy ? <Spinner /> : "Start conversation"}</Button>
        </Card>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
          <div className="flex h-[65vh] flex-col rounded-xl border border-[#27272A] bg-[#0f0f12]">
            <div className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="chat-messages">
              {msgs.map((m, i) => (
                <div key={i} className={cn("flex", isUser(m) ? "justify-end" : "justify-start")}>
                  <div className={cn("group max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed", isUser(m) ? "bg-[#D4AF37] text-black" : "border border-[#27272A] bg-[#18181B] text-zinc-200")}>
                    <p className="whitespace-pre-wrap">{msgText(m)}</p>
                    {!isUser(m) && <button onClick={() => speak(msgText(m))} className="mt-2 text-zinc-500 hover:text-[#D4AF37]" data-testid={`speak-msg-${i}`}><Volume2 className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
              ))}
              {busy && <Spinner className="text-[#D4AF37]" />}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="flex gap-2 border-t border-[#27272A] p-3">
              <Button type="button" variant="outline" size="icon" onClick={recording ? stopRec : startRec} data-testid="voice-record-button" className={recording ? "border-red-500 text-red-400" : ""}>
                {recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your reply..." className="bg-[#09090B]" data-testid="chat-input" />
              <Button type="submit" disabled={busy || !text.trim()} data-testid="chat-send-button"><Send className="h-4 w-4" /></Button>
            </form>
          </div>
          {score && <Card className="h-fit" data-testid="chat-score-result"><h3 className="mb-4 font-display text-lg text-[#F3C649]">Session score</h3><Rich value={score} /></Card>}
        </div>
      )}
    </div>
  );
}
